export const phoneRegex = /^(\+48|0048)?[ ]?\d{3}[ -]?\d{3}[ -]?\d{3}$/;

/**
 * Długości IBAN wg kraju. Sama suma kontrolna mod-97 nie wystarcza: numer o błędnej
 * długości ma ok. 1 na 97 szans przypadkowo ją spełnić, więc bez tej kontroli
 * ucięty numer bywa akceptowany.
 */
const IBAN_LENGTHS: Record<string, number> = {
  PL: 28, DE: 22, GB: 22, CZ: 24, SK: 24, LT: 20, LV: 21, EE: 20,
  FR: 27, ES: 24, IT: 27, NL: 18, BE: 16, AT: 20, IE: 22, PT: 25,
  SE: 24, DK: 18, NO: 15, FI: 18, CH: 21, LU: 20, HU: 28, RO: 24,
  BG: 22, HR: 21, SI: 19, GR: 27, UA: 29,
};

export function validateIBAN(rawIban: string): boolean {
  if (!rawIban) return false;

  let iban = rawIban.replace(/\s+/g, '').toUpperCase();

  // Dodaj domyślnie 'PL', jeśli brakuje prefixu kraju
  if (!/^[A-Z]{2}/.test(iban)) {
    iban = 'PL' + iban;
  }

  if (iban.length < 4 || !/^[A-Z0-9]+$/.test(iban)) return false;

  // Struktura: 2 litery kraju + 2 cyfry kontrolne + reszta
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) return false;

  const expectedLength = IBAN_LENGTHS[iban.slice(0, 2)];
  if (expectedLength && iban.length !== expectedLength) return false;

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numericIban = rearranged
    .split('')
    .map((char) => (/[A-Z]/.test(char) ? (char.charCodeAt(0) - 55).toString() : char))
    .join('');

  let remainder = '';
  for (let i = 0; i < numericIban.length; i += 7) {
    const part = remainder + numericIban.slice(i, i + 7);
    remainder = (BigInt(part) % BigInt(97)).toString();
  }

  return Number(remainder) === 1;
}

/**
 * Zwraca opis tego, co jest nie tak z numerem rachunku, albo null gdy numer jest poprawny.
 * Używane zamiast samego true/false, żeby użytkownik wiedział, co poprawić — "brak prefiksu PL"
 * niesie znacznie więcej niż "niepoprawny numer".
 */
export function getIbanError(rawIban: string): string | null {
  if (!rawIban || !rawIban.trim()) return 'Numer konta jest wymagany';

  const iban = rawIban.replace(/\s+/g, '').toUpperCase();

  if (/[^A-Z0-9]/.test(iban)) {
    return 'Numer może zawierać tylko litery i cyfry';
  }

  // Numer bez prefiksu kraju: podpowiadamy dopisanie PL zamiast ogólnego błędu.
  if (!/^[A-Z]{2}/.test(iban)) {
    return 'Brak prefiksu kraju - numer powinien zaczynać się od PL';
  }

  const country = iban.slice(0, 2);
  const expectedLength = IBAN_LENGTHS[country];

  if (!expectedLength) {
    return `Nieznany kod kraju "${country}"`;
  }

  if (!/^[A-Z]{2}\d{2}/.test(iban)) {
    return 'Numer rachunku jest niepoprawny';
  }

  if (iban.length !== expectedLength) {
    return iban.length < expectedLength ? 'Numer jest za krótki' : 'Numer jest za długi';
  }

  if (!validateIBAN(iban)) {
    return 'Numer rachunku jest niepoprawny';
  }

  return null;
}

// Funkcja do walidacji NIP
export const isValidNIP = (nip: string): boolean => {
    if (!/^\d{10}$/.test(nip)) return false;
    const digits = nip.split('').map(Number);
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    const control = digits.slice(0, 9).reduce((acc, digit, i) => acc + digit * weights[i], 0) % 11;
    return control === digits[9];
};

// Funkcja do walidacji REGON
export const isValidREGON = (regon: string): boolean => {
    if (!/^\d{9}$/.test(regon)) return false;
    const digits = regon.split('').map(Number);
    const weights = [8, 9, 2, 3, 4, 5, 6, 7];
    const control = digits.slice(0, 8).reduce((acc, digit, i) => acc + digit * weights[i], 0) % 11;
    return (control === 10 ? 0 : control) === digits[8];
};
